<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    scrollable
    persistent
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" dark v-bind="attrs" @click="newCampaign()">
        <v-icon left dense>mdi-plus</v-icon>
        Add campaign
      </v-btn>
    </template>
    <v-card>
      <v-toolbar dark color="primary" dense>
        <v-toolbar-title>Campaign editor</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-toolbar-items>
          <v-btn text color="white" outlined light @click="save()">Save</v-btn>
          <v-btn text color="white" light @click="clearAndClose()">Discard</v-btn
          ></v-toolbar-items
        >
      </v-toolbar>
      <v-card-text class="pt-4 pl-5 pr-5">
          <v-row>
              <h2 class="ml-2 mt-5 mb-7">Basic settings</h2>
          </v-row>
          <v-row>
            <v-form ref="campaignForm" class="ml-5 mr-2">
              <v-row>
                <v-text-field
                  required
                  label="Campaign title"
                  placeholder="Enter campaign title"
                  outlined
                  v-model="campaign.title"
                  :rules="[rules.required]"
                ></v-text-field>
                <v-text-field
                  class="ml-4"
                  required
                  label="Campaign identifier (displayed in URLs)"
                  placeholder="campaign-identifier"
                  outlined
                  v-model="campaign.slug"
                  :rules="[rules.required, rules.slug]"
                ></v-text-field>
                <v-menu
                  v-model="startDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="0"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="ml-10"
                      v-model="campaign.schedule.publish"
                      label="Start date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="campaign.schedule.publish"
                    @input="startDateMenu = false"
                  ></v-date-picker>
                </v-menu>
                <v-menu
                  v-model="endDateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      class="ml-10"
                      v-model="campaign.schedule.expire"
                      label="End date"
                      prepend-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="campaign.schedule.expire"
                    @input="endDateMenu = false"
                  ></v-date-picker>
                </v-menu>
              </v-row>
              <v-row class="d-flex justify-center align-center">
                <v-textarea
                  required
                  label="Description"
                  placeholder="Campaign description"
                  outlined
                  v-model="campaign.description"
                ></v-textarea>
                <v-switch
                  label="Campaign is published"
                  dense
                  inset
                  class="ml-10 mt-5"
                  v-model="campaignStatus"
                ></v-switch>
              </v-row>
            </v-form>
          </v-row>
          <v-row class="bottom-shadow ml-n5 mr-n5 mb-5">&nbsp;</v-row>
          <v-row>
            <h2 class="ml-2 mt-5 mb-7">Automatic selection</h2>
          </v-row>
          <v-row class="ma-1">
            <v-select
              class="w-100 pr-4"
              label="Select area"
              outlined
              :items="getAreas"
              item-text="name"
              item-value="id"
              v-model="selectedAreas"
              clearable
              multiple
              :disabled="!areasLoaded"
              :loading="!areasLoaded"
              :menu-props="{ offsetY: true }"
              style="width: 250px; height: 56px;"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  small
                  class="ma-2"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 2" class="grey--text text--darken-1 caption">
                  (+{{ selectedAreas.length - 2 }} others)
                </span>
              </template>
            </v-select>
            <v-select
              class="w-100 pr-4"
              label="Select categories"
              outlined
              :items="getCategories"
              v-model="selectedCategories"
              clearable
              item-text="name"
              item-value="id"
              multiple
              :disabled="!categoriesLoaded"
              :loading="!categoriesLoaded"
              :menu-props="{ offsetY: true }"
              style="width: 250px; height: 56px;"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 2"
                  small
                  class="ma-2"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index == 2" class="grey--text text--darken-1 caption">
                  (+{{ selectedCategories.length - 2 }} others)
                </span>
              </template>
            </v-select>
            <v-select
              class="w-100"
              label="Select seniorities"
              outlined
              :items="getSeniorities"
              v-model="selectedSeniorities"
              clearable
              item-text="name"
              item-value="id"
              multiple
              :disabled="!senioritiesLoaded"
              :loading="!senioritiesLoaded"
              :menu-props="{ offsetY: true }"
              style="width: 250px; height: 56px;"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 3"
                  small
                  class="ma-2"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 3" class="grey--text text--darken-1 caption">
                  (+{{ selectedSeniorities.length - 3 }} others)
                </span>
              </template>
            </v-select>
          </v-row>
          <v-row class="bottom-shadow ml-n5 mr-n5">&nbsp;</v-row>
          <v-row class="align-center pl-1">
            <v-col><h2 class="ml-2 mt-5 mb-7">Manual selection</h2></v-col>
            <v-col><PositionNew @jobCreated="afterJobCreated"/></v-col>
          </v-row>
          <v-row class="pr-4 pl-4">
            <v-select
              class="custom-select-width"
              label="Status"
              outlined
              :items="statuses"
              v-model="searchByStatuses"
              clearable
              item-text="text"
              item-value="value"
              multiple
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              :menu-props="{ offsetY: true }"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 1"
                  small
                  class="ma-1"
                >
                  {{ item.text }}
                </v-chip>
                <span v-else-if="index === 1" class="grey--text text--darken-1 caption">
                  (+{{ searchByStatuses.length - 1 }})
                </span>
              </template>
            </v-select>
            <v-autocomplete
              class="custom-select-width pl-4"
              label="Company"
              outlined
              :items="jobCompanies"
              v-model="searchByCompanies"
              clearable
              item-text="name"
              item-value="id"
              multiple
              :loading="!campaignJobsLoaded"
              :disabled="!campaignJobsLoaded"
              :menu-props="{ offsetY: true }"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 1"
                  class="ma-1"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 1" class="grey--text text--darken-1 caption">
                  (+{{ searchByCompanies.length - 1 }} )
                </span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              class="custom-select-width pl-4"
              label="Area"
              outlined
              :items="jobAreas"
              v-model="searchByAreas"
              clearable
              item-text="name"
              item-value="name"
              multiple
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              :menu-props="{ offsetY: true }"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 1"
                  small
                  class="ma-1"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 1" class="grey--text text--darken-1 caption">
                  (+{{ searchByAreas.length - 1 }})
                </span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              class="custom-select-width pl-4"
              label="Category"
              outlined
              :items="jobCategories"
              v-model="searchByCategories"
              clearable
              item-text="name"
              item-value="id"
              multiple
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              :menu-props="{ offsetY: true }"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 1"
                  small
                  class="ma-1"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 1" class="grey--text text--darken-1 caption">
                  (+{{ searchByCategories.length - 1 }})
                </span>
              </template>
            </v-autocomplete>
            <v-autocomplete
              class="custom-select-width pl-4"
              label="Source"
              outlined
              :items="jobSources"
              v-model="searchBySources"
              clearable
              item-text="name"
              item-value="value"
              multiple
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              :menu-props="{ offsetY: true }"
              item-key="id"
            >
              <template v-slot:selection="{ item, index }">
                <v-chip
                  v-if="index < 1"
                  small
                  class="ma-1"
                >
                  {{ item.name }}
                </v-chip>
                <span v-else-if="index === 1" class="grey--text text--darken-1 caption">
                  (+{{ searchBySources.length - 1 }})
                </span>
              </template>
            </v-autocomplete>
            <v-text-field
              class="custom-text-field-width pl-4"
              required
              placeholder="Search by job title or description"
              prepend-inner-icon="mdi-magnify"
              v-model="search"
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              outlined
            ></v-text-field>
          </v-row>
          <v-row class="pr-4 pl-4 position-relative">
            <v-data-table
              :headers="headers"
              :items="getJobs"
              :items-per-page="10"
              style="width: 100%"
              v-model="selectedJobs"
              :disabled="!campaignJobsLoaded"
              :loading="!campaignJobsLoaded"
              :show-select="campaignJobsLoaded"
              item-key="rowId"
              fixed-header
              height="auto"
            >
              <template v-slot:[`item.parsedStatus`]="{ item }">
                  <span v-for="status of Object.entries(item.parsedStatus).filter(e => e[1]).map(e => e[0]) "
                      :key="status">
                      <v-chip
                          label
                          small
                          outlined
                          class="mr-1"
                          :color="getStatusColor(status)"
                          v-if="status !== 'reviewed'">
                          {{ status }}
                      </v-chip>
                  </span>
              </template>
              <template v-slot:[`item.schedule.publish`]="{ item }">
                  <span>
                    {{ item.schedule && item.schedule.publish.split('T')[0] }}
                  </span>
              </template>
              <template v-slot:[`item.isPrivateInCampaign`]="{ item }">
                  <v-switch
                    v-if="selectedJobs.some(j => j.id === item.id)"
                    v-model="isPrivateMap[item.id]"
                    dense
                    hide-details
                    class="ma-0 pa-0"
                  ></v-switch>
              </template>
              <template v-slot:header.isPrivateInCampaign="{ header }">
                  <div class="d-flex align-center hiddenFromPublicColumn">
                    <v-checkbox
                      v-model="allJobsHiddenFromPublic"
                      :indeterminate="isSomeJobsHidden"
                      hide-details
                      dense
                      class="mr-2 custom-checkbox"
                    ></v-checkbox>
                    <span class="custom-text">{{ header.text }}</span>
                  </div>
              </template>
            </v-data-table>
            <div class="selected-info d-flex justify-end pa-2 text-caption">
              Selected jobs: {{ selectedJobs.length }}
            </div>
          </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>
<script>
import PositionNew from '@/components/PositionNew.vue';

export default {
  components: {
    PositionNew,
  },
  props: [
    'editCampaign',
    'showDialog',
  ],
  data() {
    return {
      dialog: false,
      startDateMenu: false,
      endDateMenu: false,
      oldId: null,
      campaign: {
        id: '',
        title: '',
        description: '',
        imageUrl: '',
        externalUrl: '',
        terms: {
          show: false,
          title: '',
          description: '',
          externalUrl: '',
        },
        schedule: {
          publish: new Date().toISOString().substr(0, 10),
          expire: new Date().toISOString().substr(0, 10),
        },
        status: 'draft',
        privateJobs: false,
        filters: {
          areas: [],
          categories: [],
          tags: [],
          seniorities: [],
        },
      },
      selectedJobs: [],
      headers: [
        { text: 'Publish date', value: 'schedule.publish', width: '120px' },
        { text: 'Company', value: 'employer.name' },
        { text: 'Job title', value: 'title', width: '300px' },
        { text: 'Seniority', value: 'clustering.seniority.id' },
        { text: 'City', value: 'location.city' },
        { text: 'Area', value: 'location.area' },
        { text: 'Status', value: 'parsedStatus' },
        { text: 'Hidden from public listing', value: 'isPrivateInCampaign', sortable: false },

      ],
      statuses: [
        { text: 'Reviewed', value: 'reviewed' },
        { text: 'Unreviewed', value: 'unreviewed' },
        { text: 'Hidden', value: 'hidden' },
        { text: 'User submitted', value: 'userSubmitted' },
        { text: 'Reported', value: 'reported' },
        { text: 'Active', value: 'active' },
        { text: 'Expired', value: 'expired' },
      ],
      search: '',
      searchTerms: {
        keywords: '',
        area: '',
      },
      rules: {
        required: value => !!value || 'This field is required.',
        slug: value => /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value) || 'Invalid slug format. Use lowercase letters, numbers and hyphens only.',
      },
      // for search in data table
      jobCompanies: [],
      jobCategories: [],
      jobAreas: [],
      jobSources: [],
      searchByStatuses: ['active'],
      searchByCompanies: [],
      searchByAreas: [],
      searchByCategories: [],
      searchBySources: [],
      // to indicate the loading of data
      areasLoaded: false,
      categoriesLoaded: false,
      senioritiesLoaded: false,
      campaignJobsLoaded: false,
      jobs: [],
      createdJobId: undefined,
      isPrivateMap: {},
    };
  },
  mounted() {
    this.$store.dispatch('dispatchGetUsers');
    this.$store.dispatch('dispatchCategoriesWithDetails')
      .then(() => {
        this.areasLoaded = true;
        this.categoriesLoaded = true;
        this.senioritiesLoaded = true;
      });
    this.$store.dispatch('dispatchSeniorityLevels');
  },
  watch: {
    editCampaign(newVal) {
      if (newVal !== null) {
        this.oldId = newVal.id;
        this.campaign = newVal;

        // Populate search terms from state
        this.searchTerms.keywords = this.$store.state.searchTerms.keywords;
        this.searchTerms.area = this.$store.state.searchTerms.area;

        // Ensure filters object exists and has all properties (for backwards compatibility)
        if (!this.campaign.filters) {
          this.campaign.filters = {
            areas: [], categories: [], tags: [], seniorities: [],
          };
        }
        if (!this.campaign.filters.areas) this.campaign.filters.areas = [];
        if (!this.campaign.filters.categories) this.campaign.filters.categories = [];
        if (!this.campaign.filters.tags) this.campaign.filters.tags = [];
        if (!this.campaign.filters.seniorities) this.campaign.filters.seniorities = [];
      }
    },
    showDialog(newVal) {
      this.dialog = newVal;
      if (newVal) {
        this.resetSearchFilters();
        // Fetch jobs for picker
        this.fetchAllJobs();
      }
    },
  },
  methods: {
    save() {
      // Clean up
      if (!this.campaign.filters.areas || this.campaign.filters.areas[0] == null) {
        this.campaign.filters.areas = [];
      }
      if (!this.campaign.filters.categories || this.campaign.filters.categories[0] == null) {
        this.campaign.filters.categories = [];
      }
      if (!this.campaign.filters.tags || this.campaign.filters.tags[0] == null) {
        this.campaign.filters.tags = [];
      }
      if (!this.campaign.filters.seniorities || this.campaign.filters.seniorities[0] == null) {
        this.campaign.filters.seniorities = [];
      }

      if (this.editCampaign !== undefined && this.oldId !== null) {
        this.$store
          .dispatch('dispatchPutCampaign', { campaignData: this.campaign, id: this.oldId })
          .then(() => {
            // Commit job list to campaign
            this.$store
              .dispatch('dispatchPutAdminCampaignJobs', {
                campaignId: this.campaign.id,
                jobIds: this.selectedJobs.map(job => ({ id: job.id, private: !!this.isPrivateMap[job.id] })),
              }).then(() => {
                this.$store.dispatch('dispatchGetCampaigns');
                this.clearAndClose();
              })
              .catch((error) => {
                console.error(error);
              });
          })
          .catch((error) => {
            console.error(error);
          });
      } else {
        // Set campaign slug as id
        this.campaign.id = this.campaign.slug;
        this.$store
          .dispatch('dispatchPostCampaign', { campaignData: this.campaign })
          .then((createdCampaign) => {
            // Update with the actual created campaign ID
            this.campaign.id = createdCampaign.id;
            // Commit job list to campaign
            this.$store
              .dispatch('dispatchPutAdminCampaignJobs', {
                campaignId: this.campaign.id,
                jobIds: this.selectedJobs.map(job => ({ id: job.id, private: !!this.isPrivateMap[job.id] })),
              })
              .then(() => {
                this.$store.dispatch('dispatchGetCampaigns');
                this.clearAndClose();
              })
              .catch((error) => {
                console.error(error);
              });
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
    clearAndClose() {
      this.$emit('campaignCreated', this.campaign);
      this.dialog = false;
      this.clearCampaign();
    },
    newCampaign() {
      this.clearCampaign();
      this.dialog = true;
      this.fetchAllJobs();
    },
    clearCampaign() {
      // Reset campaign object
      this.campaign = {
        id: '',
        title: '',
        description: '',
        imageUrl: '',
        externalUrl: '',
        terms: {
          show: false,
          title: '',
          description: '',
          externalUrl: '',
        },
        schedule: {
          publish: new Date().toISOString().substr(0, 10),
          expire: new Date().toISOString().substr(0, 10),
        },
        status: 'draft',
        privateJobs: false,
        filters: {
          areas: [],
          categories: [],
          tags: [],
          seniorities: [],
        },
      };
      this.resetSearchFilters();
      this.searchTerms = {
        keywords: '',
        area: '',
      };
      this.selectedJobs = [];
      this.oldId = null;
      // Reset search terms
      this.$store.dispatch('commitSearchTerms', this.searchTerms);
      this.$store.dispatch('commitSearchResults', this.searchTerms);
      this.createdJobId = undefined;
      this.isPrivateMap = {};
    },
    resetSearchFilters() {
      this.searchByStatuses = ['active'];
      this.searchByCompanies = [];
      this.searchByAreas = [];
      this.searchByCategories = [];
      this.searchBySources = [];
      this.search = '';
    },
    fetchAllJobs() {
      this.areasLoaded = false;
      this.categoriesLoaded = false;
      this.senioritiesLoaded = false;
      this.jobs = [];
      this.$store.dispatch('dispatchGetJobsAdmin').then((receivedJobs) => {
        // Helper function to extract unique values and map them to { name, id }
        function extractUniqueItems(items, key) {
          const seen = new Set();
          return items
            .filter(item => item !== undefined && item !== null) // Ensure item exists
            .reduce((acc, item) => {
              const value = item[key]; // Extract the desired key (e.g., name, area, etc.)
              if (!seen.has(value)) {
                seen.add(value);
                acc.push({
                  name: value,
                  id: item.id || acc.length, // Auto-increment ID
                });
              }
              return acc;
            }, []);
        }
        const SOURCE_GROUPS = [
          { prefix: 'test-api', name: 'Manually added', value: 'test-api' },
          { prefix: 'monster', name: 'Jobly', value: 'monster' },
          { prefix: 'academicpositions', name: 'Academic Positions', value: 'academicpositions' },
          { prefix: 'aalto', name: 'Aalto University', value: 'aalto' },
          { prefix: 'tyomarkkinatori', name: 'Työmarkkinatori', value: 'tyomarkkinatori' },
          { prefix: 'microsoft', name: 'Microsoft', value: 'microsoft' },
        ];
        const normalizeSourceApiId = (apiId) => {
          const group = SOURCE_GROUPS.find(group => apiId.startsWith(group.prefix));
          return group ? group.value : apiId;
        };
        const displaySourceName = (apiId) => {
          const group = SOURCE_GROUPS.find(group => apiId.startsWith(group.prefix));
          return group ? group.name : apiId;
        };

        this.jobs = receivedJobs.map((job, index) => ({
          ...job,
          rowId: `${job.id}-${job.employer.location.city}-${index}`,
        }));
        this.jobCompanies = extractUniqueItems(this.jobs.map(job => job.employer), 'name');
        this.jobAreas = extractUniqueItems(this.jobs.map(job => job.employer.location), 'area');
        this.jobCategories = extractUniqueItems(this.jobs.flatMap(job => job.clustering.categories), 'name');
        this.jobSources = this.jobs
          .map(job => (job.source && job.source.apiId) || '')
          .filter(apiId => apiId)
          .reduce((acc, apiId) => {
            const value = normalizeSourceApiId(apiId);
            if (!acc.some(item => item.value === value)) {
              acc.push({ id: value, name: displaySourceName(apiId), value });
            }
            return acc;
          }, []);

        this.areasLoaded = true;
        this.categoriesLoaded = true;
        this.senioritiesLoaded = true;

        if (this.campaign && this.campaign.id) {
          this.campaignJobsLoaded = false;
          this.selectedCategories = this.campaign.filters.categories;
          this.selectedAreas = this.campaign.filters.areas;

          return this.$store.dispatch('dispatchGetAdminCampaignJobs', { campaignId: this.campaign.id }).then((campaignJobs) => {
            const campaignJobsMap = new Map(campaignJobs.map(cj => [cj.id, cj.private]));

            if (this.createdJobId) {
              campaignJobsMap.set(this.createdJobId, false);
              this.createdJobId = undefined;
            }

            this.isPrivateMap = {};
            this.jobs.forEach((job) => {
              this.$set(this.isPrivateMap, job.id, campaignJobsMap.get(job.id) || false);
            });

            if (campaignJobsMap.size > 0) {
              this.selectedJobs = this.getJobs.filter(j => campaignJobsMap.has(j.id));

              const selectedJobIds = this.selectedJobs.map(job => job.id);

              if (selectedJobIds.length > 0) {
                this.jobs.sort((a, b) => {
                  const aIndex = selectedJobIds.indexOf(a.id);
                  const bIndex = selectedJobIds.indexOf(b.id);
                  // If both are selected, maintain original order
                  if (aIndex !== -1 && bIndex !== -1) {
                    return 0;
                  }
                  // If only a is selected, put it first
                  if (aIndex !== -1) {
                    return -1;
                  }
                  // If only b is selected, put it first
                  if (bIndex !== -1) {
                    return 1;
                  }
                  // If neither is selected, maintain original order
                  return 0;
                });
              }
            } else {
              this.selectedJobs = [];
            }
            this.campaignJobsLoaded = true;
          }).finally(() => {
            this.campaignJobsLoaded = true;
          });
        }
        return null;
      }).finally(() => {
        this.campaignJobsLoaded = true;
        this.areasLoaded = true;
        this.categoriesLoaded = true;
        this.senioritiesLoaded = true;
      });
    },
    getStatusColor(status) {
      switch (status) {
        case 'reviewed':
          return 'blue';
        case 'unreviewed':
          return 'orange';
        case 'hidden':
          return 'default';
        case 'userSubmitted':
          return 'info';
        case 'reported':
          return 'error';
        case 'active':
          return 'success';
        case 'expired':
          return 'error';
        default:
          return 'grey';
      }
    },
    capitalizeFirstLetter(str) {
      if (!str) return ''; // Handle empty or undefined values
      return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    },
    parseStatus(job) {
      return {
        reviewed: job.status.reviewStatus !== 'unreviewed',
        unreviewed: job.status.reviewStatus === 'unreviewed',
        hidden: Object.keys(job.status.hiddenBecause).length > 0,
        userSubmitted: job.source.apiId.includes('jobsinfinland.fi'),
        reported: Object.keys(job.status.reportedBecause).length > 0,
        active:
          Object.keys(job.status.hiddenBecause).length === 0
          && new Date(job.schedule.expire).getTime() > Date.now(),
        expired: new Date(job.schedule.expire).getTime() < Date.now(),
      };
    },
    afterJobCreated(jobData) {
      this.createdJobId = jobData ? jobData.id : undefined;
      this.fetchAllJobs();
    },
    toggleAllHiddenFromPublic(value) {
      this.getVisibleSelectedJobs.forEach((job) => {
        this.$set(this.isPrivateMap, job.id, value);
      });
    },
  },
  computed: {
    getJobs() {
      const jobs = this.jobs ? [...this.jobs] : [];
      return jobs.map((job) => {
        const parsedStatus = this.parseStatus(job);
        const clustering = job.clustering || {};
        const seniority = clustering.seniority || {};

        return {
          ...job,
          parsedStatus,
          isPrivateInCampaign: !!this.isPrivateMap[job.id],
          clustering: {
            ...clustering,
            seniority: {
              ...seniority,
              id: this.capitalizeFirstLetter(seniority.id),
            },
          },
        };
      }).filter((job) => {
        if (this.searchByStatuses.length > 0) {
          // eslint-disable-next-line max-len
          const jobStatuses = Object.entries(job.parsedStatus).filter(([, value]) => value).map(([key]) => key.toUpperCase());
          // eslint-disable-next-line max-len
          if (!this.searchByStatuses.map(status => status.toUpperCase()).some(status => jobStatuses.includes(status))) {
            return false;
          }
        }
        // Filter by companies if searchByCompanies is not empty
        if (this.searchByCompanies.length > 0) {
          // company may have different id, so lets gather all its ids
          const jobCompany = (job.employer && job.employer.name) || '';
          const jobCompanyIds = this.jobCompanies
            .filter(company => company.name === jobCompany).map(company => company.id);

          if (!this.searchByCompanies.some(companyId => jobCompanyIds.includes(companyId))) {
            return false;
          }
        }

        // Filter by areas if searchByAreas is not empty
        if (this.searchByAreas.length > 0) {
          const jobArea = (job.employer && job.employer.location && job.employer.location.area) || '';
          const matchedArea = this.jobAreas.find(({ name }) => name === jobArea);
          const areaId = matchedArea ? matchedArea.id : null;

          if (areaId === null || !this.searchByAreas.includes(jobArea)) {
            return false;
          }
        }

        // Filter by categories if searchByCategories is not empty
        if (this.searchByCategories.length > 0) {
          const jobCategories = (job.clustering && job.clustering.categories
            .flatMap(({ id }) => id)) || [];

          if (!this.searchByCategories.some(category => jobCategories.includes(category))) {
            return false;
          }
        }

        // Filter by sources if searchBySources is not empty
        if (this.searchBySources.length > 0) {
          const jobSource = (job.source && job.source.apiId) || '';
          if (!this.searchBySources.some(sourcePrefix => jobSource.startsWith(sourcePrefix))) {
            return false;
          }
        }

        // filter by either job title or description
        if (this.search.length > 0) {
          const searchLower = this.search.toLowerCase();
          // eslint-disable-next-line max-len
          if (!job.title.toLowerCase().includes(searchLower) && !job.description.toLowerCase().includes(searchLower)) {
            return false;
          }
        }

        return true;
      });
    },
    getCategories() {
      return this.$store.state.categoriesWithDetails;
    },
    getAreas() {
      return this.$store.state.mapData;
    },
    getSeniorities() {
      return this.$store.state.seniorityLevels || [];
    },
    getStatuses() {
      return this.$store.state.statuses || [];
    },
    users() {
      return this.$store.state.users.map(user => ({
        text: `${user.firstName} ${user.lastName}`,
        value: user.id,
      }));
    },
    selectedAreas: {
      get() {
        return this.searchTerms.area && this.searchTerms.area.length > 0 ? this.searchTerms.area.split('|') : [];
      },
      set(areas) {
        if (areas !== null) {
          this.searchTerms.area = areas.join('|');
          this.campaign.filters.areas = areas;
        } else {
          this.searchTerms.area = null;
          this.campaign.filters.areas = [];
        }
      },
    },
    selectedCategories: {
      get() {
        return this.searchTerms.keywords && this.searchTerms.keywords.length > 0 ? this.searchTerms.keywords.split('|') : [];
      },
      set(categories) {
        if (categories !== null) {
          this.searchTerms.keywords = categories.join('|');
          this.campaign.filters.categories = categories;
        } else {
          this.searchTerms.keywords = null;
          this.campaign.filters.categories = [];
        }
      },
    },
    selectedSeniorities: {
      get() {
        return this.campaign.filters.seniorities || [];
      },
      set(seniorities) {
        if (seniorities !== null) {
          this.campaign.filters.seniorities = seniorities;
        } else {
          this.campaign.filters.seniorities = [];
        }
      },
    },
    campaignStatus: {
      get() {
        return this.campaign.status === 'published';
      },
      set(status) {
        this.campaign.status = status ? 'published' : 'draft';
      },
    },
    allJobsHiddenFromPublic: {
      get() {
        const targets = this.getVisibleSelectedJobs;
        if (targets.length === 0) return false;
        return targets.every(job => this.isPrivateMap[job.id]);
      },
      set(value) {
        this.toggleAllHiddenFromPublic(value);
      },
    },
    isSomeJobsHidden() {
      const targets = this.getVisibleSelectedJobs;
      if (targets.length === 0) return false;
      const hiddenCount = targets.filter(job => this.isPrivateMap[job.id]).length;
      return hiddenCount > 0 && hiddenCount < targets.length;
    },
    getVisibleSelectedJobs() {
      const selectedIds = new Set(this.selectedJobs.map(j => j.id));
      return this.getJobs.filter(j => selectedIds.has(j.id));
    },
  },
};
</script>
<style scoped>
h2 {
  color: rgba(0, 45, 160, 1);
}
.campaign-header {
  background: rgba(0, 45, 160, 1);
  color: #fff;
}
.campaign-header h2 {
  color: #fff;
  text-transform: uppercase;
  font-size: 24px;
}
.campaign-header p {
  font-size: 18px;
}
.bottom-shadow {
  box-shadow: 0px 2px 3px -10px rgba(0, 0, 0, 0.2),
              0px 4px 5px 0px rgba(0, 0, 0, 0.14);
  border-width: 0;
}
.custom-select-width {
  width: 70px !important;
}
.position-relative {
  position: relative;
}
.selected-info {
  position: absolute;
  left: 25px;
  bottom: 35px;
  transform: translateY(50%);
  background-color: white;
  padding: 8px 16px;
  border-radius: 4px;
  z-index: 1;
  font-size: 0.95em !important;
}
.hiddenFromPublicColumn {
  display: flex;
  height: 100%;
  align-items: center;
  gap: 8px;
  padding: 0;
}

.hiddenFromPublicColumn .custom-switch {
  margin-top: 0 !important;
}
.hiddenFromPublicColumn  .custom-text {
  margin-top: 0 !important;
}
.v-data-table-header__icon {
  margin-left: auto !important;
  align-self: center !important;
}

::v-deep th[aria-label^="Hidden from public"] {
  display: flex !important;
  align-items: center;
  font-size: 18px;
}
</style>
